// src/App.js

import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { GradesProvider } from "./contexts/GradesContext";
import Header from "./components/Header";
import Aurora from "./Aurora";
import DeferredAnalytics from "./components/DeferredAnalytics";
import Footer from "./components/Footer";
import Calculator from "./pages/calculator";
import { MOTION_REDUCED } from "./shared/constants/motion";
import "./index.css";

const ArticlesPage = lazy(() => import("./features/content/pages/ArticlesPage"));
const ArticlePage = lazy(() => import("./features/content/pages/ArticlePage"));
const LegacyBlogRedirect = lazy(() => import("./features/content/pages/LegacyBlogRedirect"));
const About = lazy(() => import("./pages/about"));
const FAQ = lazy(() => import("./pages/qa"));
const PrivacyPolicy = lazy(() => import("./pages/privacy-policy"));
const Contact = lazy(() => import("./pages/contact"));
const TutorsPage = lazy(() => import("./features/tutors/pages/TutorsPage"));
const BecomeTutorForm = lazy(() => import("./features/tutors/pages/TutorApplicationPage"));
const FindTutorPage = lazy(() => import("./pages/FindTutorPage"));
const SubjectsPage = lazy(() => import("./features/subjects/pages/SubjectsPage"));
const SubjectPage = lazy(() => import("./features/subjects/pages/SubjectPage"));
const ResourcesPage = lazy(() => import("./features/resources/pages/ResourcesPage"));
const GradeDescriptorsPage = lazy(() => import("./features/resources/pages/GradeDescriptorsPage"));
const GradeDescriptorsIndexPage = lazy(() => import("./features/resources/pages/GradeDescriptorsIndexPage"));
const BoundariesIndexPage = lazy(() => import("./features/resources/pages/BoundariesIndexPage"));
const SubjectBoundaryPage = lazy(() => import("./features/resources/pages/SubjectBoundaryPage"));
const TimezoneFinderPage = lazy(() => import("./features/resources/pages/TimezoneFinderPage"));
const IbDanishGradeConverterPage = lazy(() => import("./features/resources/pages/IbDanishGradeConverterPage"));
const IbGermanAbiturConverterPage = lazy(() => import("./features/resources/pages/IbGermanAbiturConverterPage"));
const IbGpaConverterPage = lazy(() => import("./features/resources/pages/IbGpaConverterPage"));
const IbIndianPercentageConverterPage = lazy(() => import("./features/resources/pages/IbIndianPercentageConverterPage"));
const IbItalianGradeConverterPage = lazy(() => import("./features/resources/pages/IbItalianGradeConverterPage"));
const IbGradingPromptPage = lazy(() => import("./features/resources/pages/IbGradingPromptPage"));
const IbPolishMaturaConverterPage = lazy(() => import("./features/resources/pages/IbPolishMaturaConverterPage"));
const IbSpanishGradeConverterPage = lazy(() => import("./features/resources/pages/IbSpanishGradeConverterPage"));
const IbUcasTariffConverterPage = lazy(() => import("./features/resources/pages/IbUcasTariffConverterPage"));
const ConvertersIndexPage = lazy(() => import("./features/resources/pages/ConvertersIndexPage"));

// Copy of the build-time prerendered route markup, shown while the first
// route's lazy chunk loads so the page keeps its height instead of collapsing.
const INITIAL_PATHNAME = window.location.pathname;
const PRERENDERED_ROUTE_HTML = document.querySelector("#root main")?.innerHTML ?? "";

function PrerenderedRouteFallback() {
  if (!PRERENDERED_ROUTE_HTML) {
    return null;
  }

  return <div className="contents" dangerouslySetInnerHTML={{ __html: PRERENDERED_ROUTE_HTML }} />;
}

function getRouteTransitionKey(pathname) {
  const subjectMatch = pathname.match(/^\/subjects\/([^/]+)(?:\/(?:sl|hl))?\/?$/i);

  if (subjectMatch) {
    return `/subjects/${subjectMatch[1]}`;
  }

  const boundaryMatch = pathname.match(/^\/resources\/boundaries\/(.+?)(?:-(?:sl|hl))?\/?$/i);

  if (boundaryMatch) {
    return `/resources/boundaries/${boundaryMatch[1]}`;
  }

  if (/^\/resources\/assesment-criteria\/[^/]+\/?$/i.test(pathname)) {
    return "/resources/assesment-criteria";
  }

  return pathname;
}

export default function App() {
  const location = useLocation();
  const routeTransitionKey = getRouteTransitionKey(location.pathname);
  const isCalculatorPage = location.pathname === "/";
  const isArticlesPage = location.pathname.replace(/\/+$/, "") === "/articles";
  const isArticleReadingPage = /^\/articles\/[^/]+\/?$/.test(location.pathname);
  const isPrivacyPolicyPage = location.pathname === "/privacy-policy";

  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useLayoutEffect(() => {
    const path = location.pathname;
    document.documentElement.dataset.uiTheme = isCalculatorPage ? "light" : "dark";
    document.documentElement.dataset.uiAccent = isCalculatorPage ? "red"
      : path.startsWith("/tutors") || path === "/contact" ? "coral"
      : path === "/about" || path === "/blog" || path === "/articles" || path.startsWith("/articles/") ? "pink" : "violet";
  }, [isCalculatorPage, location.pathname]);

  const shouldShowAurora = !isCalculatorPage && !isPrivacyPolicyPage && !isArticleReadingPage && !reducedMotion;

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [routeTransitionKey]);

  return (
    <MotionConfig reducedMotion={MOTION_REDUCED}>
      <GradesProvider>
        {shouldShowAurora && (
          <div className="fixed inset-0 z-[5] pointer-events-none">
            <Aurora
              colorStops={["#3A29FF", "#FF94B4", "#FF3232"]}
              blend={0.5}
              amplitude={1.0}
              speed={0.5}
            />
          </div>
        )}
        <div className={`min-h-screen flex flex-col bg-black ${isArticlesPage || isArticleReadingPage ? "overflow-visible" : isCalculatorPage ? "overflow-x-clip" : "overflow-x-hidden"}`}>
          <Header />

          <main className="flex-grow">
              <Suspense
                fallback={location.pathname === INITIAL_PATHNAME ? <PrerenderedRouteFallback /> : null}
              >
              <Routes location={location} key={routeTransitionKey}>
                  <Route path="/" element={<Calculator />} />
                  <Route path="/articles" element={<ArticlesPage />} />
                  <Route path="/articles/:slug" element={<ArticlePage />} />
                  <Route path="/blog" element={<LegacyBlogRedirect />} />
                  <Route path="/subjects" element={<SubjectsPage />} />
                  <Route path="/subjects/:slug" element={<SubjectPage />} />
                  <Route path="/subjects/:slug/:levelSlug" element={<SubjectPage />} />

                  <Route path="/resources" element={<ResourcesPage />} />
                  <Route path="/resources/grade-descriptors" element={<GradeDescriptorsIndexPage />} />
                  <Route path="/resources/grade-descriptors/:descriptorSlug" element={<GradeDescriptorsPage />} />
                  <Route path="/resources/boundaries" element={<BoundariesIndexPage />} />
                  <Route
                    path="/resources/boundaries/:slug"
                    element={<SubjectBoundaryPage />}
                  />
                  <Route path="/resources/timezone-finder" element={<TimezoneFinderPage />} />
                  <Route path="/resources/converter" element={<ConvertersIndexPage />} />
                  <Route path="/resources/converter/ib-to-gpa" element={<IbGpaConverterPage />} />
                  <Route
                    path="/resources/converter/ib-to-polish-matura"
                    element={<IbPolishMaturaConverterPage />}
                  />
                  <Route
                    path="/resources/converter/ib-to-spanish-grade"
                    element={<IbSpanishGradeConverterPage />}
                  />
                  <Route
                    path="/resources/converter/ib-to-danish-grade"
                    element={<IbDanishGradeConverterPage />}
                  />
                  <Route
                    path="/resources/converter/ib-to-german-abitur"
                    element={<IbGermanAbiturConverterPage />}
                  />
                  <Route
                    path="/resources/converter/ib-to-italian-grade"
                    element={<IbItalianGradeConverterPage />}
                  />
                  <Route
                    path="/resources/converter/ib-to-indian-percentage"
                    element={<IbIndianPercentageConverterPage />}
                  />
                  <Route
                    path="/resources/converter/ib-to-ucas-tariff"
                    element={<IbUcasTariffConverterPage />}
                  />
                  <Route path="/resources/assesment-criteria" element={<IbGradingPromptPage />} />
                  <Route
                    path="/resources/assesment-criteria/:criteriaSlug"
                    element={<IbGradingPromptPage />}
                  />

                  <Route path="/tutors" element={<TutorsPage />} />
                  <Route path="/tutors/become" element={<BecomeTutorForm />} />
                  <Route path="/tutors/find" element={<FindTutorPage />} />

                  <Route path="/about" element={<About />} />
                  <Route path="/qa" element={<FAQ />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                </Routes>
              </Suspense>
          </main>

          <Footer isCalculatorPage={isCalculatorPage} />
          <DeferredAnalytics />
        </div>
      </GradesProvider>
    </MotionConfig>
  );
}
